import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {
  VisitBucketKey,
  VisitFrequencyCounts,
  VisitFrequencyMode,
  VisitThresholds,
} from "../../types/visit-frequency.type";
import {
  BUCKETS_BY_MODE,
  BUCKET_LABEL,
  formatCount,
  formatPercent,
  thresholdText,
} from "./visitFrequency";

interface KpiCardsProps {
  counts: VisitFrequencyCounts;
  mode: VisitFrequencyMode;
  thresholds: VisitThresholds;
  workingDays: number;
  /** When given, a bucket card opens the shop list for that bucket. */
  onBucketClick?: (bucket: VisitBucketKey) => void;
}

/** Headline figures for the current scope — all branches, or the one branch opened. */
export default function KpiCards({
  counts,
  mode,
  thresholds,
  workingDays,
  onBucketClick,
}: Readonly<KpiCardsProps>) {
  const buckets = BUCKETS_BY_MODE[mode];

  return (
    <Grid container spacing={2} mb={2}>
      <Grid size={{ xs: 12, sm: 6, md: "grow" }}>
        <Card
          title="ร้านค้าทั้งหมด"
          value={formatCount(counts.shops)}
          caption={`ไม่นับร้านพักใช้งาน ${formatCount(counts.suspended_shops)} ร้าน`}
          emphasis
        />
      </Grid>
      {buckets.map((bucket) => {
        const count = counts.buckets[bucket] ?? 0;
        return (
          <Grid key={bucket} size={{ xs: 6, sm: 4, md: "grow" }}>
            <Card
              title={BUCKET_LABEL[bucket]}
              value={formatCount(count)}
              percent={formatPercent(count, counts.shops)}
              caption={thresholdText(bucket, mode, thresholds, workingDays)}
              tone={bucket === "none" ? "warning" : undefined}
              onClick={onBucketClick ? () => onBucketClick(bucket) : undefined}
            />
          </Grid>
        );
      })}
      <Grid size={{ xs: 6, sm: 4, md: "grow" }}>
        <Card
          title="เข้าแต่ขายไม่ได้"
          value={formatCount(counts.visited_not_sold_shops)}
          percent={formatPercent(counts.visited_not_sold_shops, counts.shops)}
          caption="ร้านที่มีวันเข้าแต่ขายไม่ได้ ≥ 1 วัน"
        />
      </Grid>
    </Grid>
  );
}

interface CardProps {
  title: string;
  value: string;
  percent?: string;
  caption?: string;
  emphasis?: boolean;
  tone?: "warning";
  onClick?: () => void;
}

function Card({ title, value, percent, caption, emphasis, tone, onClick }: Readonly<CardProps>) {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") onClick();
            }
          : undefined
      }
      sx={{
        p: 1.5,
        height: "100%",
        cursor: onClick ? "pointer" : "default",
        borderColor: tone === "warning" ? "warning.light" : undefined,
        "&:hover": onClick ? { borderColor: "primary.main" } : undefined,
      }}
    >
      <Typography variant="body2" color="text.secondary" noWrap>
        {title}
      </Typography>
      <Box display="flex" alignItems="baseline" gap={1}>
        <Typography variant={emphasis ? "h5" : "h6"} fontWeight={700}>
          {value}
        </Typography>
        {percent && (
          <Typography
            variant="body2"
            fontWeight={600}
            color={tone === "warning" ? "warning.dark" : "text.secondary"}
          >
            {percent}
          </Typography>
        )}
      </Box>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Paper>
  );
}
